import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Container, Grid, Card, Box, IconButton, CardContent, CardActionArea, Typography, Tooltip, Divider, Modal, Fade } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Snackbar } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { getUIImagePath, UI_labels } from './musicUtils';
import { useFirebase } from './Firebase';
import { apiFetch } from './api';

const CustomizeEditSv7 = () => {
  const { setting } = useParams();
  const [options, setOptions] = useState([]);
  const playingAudio = useRef({ audio: null, id: null, soundNumber: null });
  const [playingId, setPlayingId] = useState({ id: null, soundNumber: null });
  const { auth, user } = useFirebase();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      const response = await apiFetch(`/api/customizationOptions/${setting}`);
      const data = await response.json();
      if (data && data.data) {
        setOptions(data.data);
      } else {
        console.error('Failed to fetch options:', data);
      }
    };

    fetchOptions();
  }, [setting]);

  const handlePlay = (option, soundNumber) => {
    const id = typeof option === 'object' ? option.id : option;

    let filename;
    if (setting.startsWith('stamp_')) {
      filename = `/content/audio/stamp/${option.filename_se}.mp3`;
    } else {
      filename = `/content/audio/bgm/custom_${String(id).padStart(2, '0')}_${soundNumber}.mp3`;
    }

    if (playingAudio.current.audio) {
      playingAudio.current.audio.pause();
      playingAudio.current.audio.currentTime = 0;
    }

    if (playingAudio.current.id !== id || playingAudio.current.soundNumber !== soundNumber) {
      const audio = new Audio(filename);
      audio.volume = 0.5;
      audio.play();

      audio.addEventListener('ended', () => {
        playingAudio.current = { audio: null, id: null, soundNumber: null };
        setPlayingId({ id: null, soundNumber: null });
      });

      playingAudio.current = { audio, id, soundNumber };
      setPlayingId({ id, soundNumber });
    } else {
      playingAudio.current = { audio: null, id: null, soundNumber: null };
      setPlayingId({ id: null, soundNumber: null });
    }
  };

  useEffect(() => {
    return () => {
      if (playingAudio.current.audio) {
        playingAudio.current.audio.pause();
        playingAudio.current.audio.currentTime = 0;
      }
    };
  }, []);

  const handleSelect = (option) => {
    setSelectedOption(option);
    if (setting.startsWith('stamp_')) {
      setModalOpen(true);
    } else {
      saveOption(option);
    }
  };

  const handleDefault = () => {
    handleSelect({ id: 0 });
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setModalOpen(false);
    saveOption(selectedOption, slot);
  };

  const saveOption = (option, slot) => {
    const finalSetting = setting.startsWith('stamp_') ? `stamp_${slot}` : setting;
    const id = typeof option === 'object' ? option.id : option;

    apiFetch('/api/sv7/customizationSave', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ setting: finalSetting, option: id }),
    })
      .then((response) => response.json())
      .then((data) => {
        setSnackbarMessage(data.message);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch((error) => {
        console.error('Error:', error);
        setSnackbarMessage('Failed to save option');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  return (
    <Container component="main" maxWidth="md">
      <div>
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 2 }}>
          <Box>
            <Typography variant="h3" align="left" sx={{ fontWeight: 'bold' }}>
              {UI_labels[setting]}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!setting.startsWith('stamp_') && (
              <Button variant="contained" color="secondary" sx={{ fontWeight: 'bold', mr: 2 }} onClick={handleDefault}>
                Default
              </Button>
            )}
            <Button variant="contained" color="primary" sx={{ fontWeight: 'bold' }} startIcon={<ArrowBack />} onClick={() => window.location.href = '/sv7/customize'}>
              Back
            </Button>
          </Box>
        </Box>
        <Divider sx={{mt: 1, mb: 2}} />
        <Grid container spacing={0.75}>
        {
          [...options].sort((a, b) => Number(a) - Number(b)).map((option) => {
            const id = typeof option === 'object' ? option.id : option;
            const filename = typeof option === 'object' ? option.filename : `item_${setting}_${String(option).padStart(4, '0')}`;
            const imagePath = getUIImagePath(setting, typeof option === 'object' ? filename : option);

            return (
              <Grid item xs={6} sm={3} key={id}>
                <Card>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ flexGrow: 1, p: 1, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                      <img src={imagePath} alt={filename} style={{ maxWidth: '100%' }} />
                    </Box>
                    <Box sx={{ p: 0.5, mt: -0.5, mb: 0.25, display: 'flex', justifyContent: 'flex-end' }}>
                      {(setting === 'bgm' || option.filename_se) && (
                        <>
                          {setting === 'bgm' ? (
                            <Tooltip title="BGM">
                              <IconButton onClick={() => handlePlay(option, 0)} style={{ marginRight: 'auto' }}>
                                {playingId.id === id && playingId.soundNumber === 0 ? <StopIcon /> : <PlayArrowIcon />}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <IconButton onClick={() => handlePlay(option, 0)} style={{ marginRight: 'auto' }}>
                              {playingId.id === id && playingId.soundNumber === 0 ? <StopIcon /> : <PlayArrowIcon />}
                            </IconButton>
                          )}
                          {setting === 'bgm' && (
                            <Tooltip title="Song Select">
                              <IconButton onClick={() => handlePlay(option, 1)} style={{ marginRight: 'auto' }}>
                                {playingId.id === id && playingId.soundNumber === 1 ? <StopIcon /> : <PlayArrowIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      <IconButton onClick={() => handleSelect(option)}>
                        <CheckIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {setting.startsWith('stamp_') && (
          <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            closeAfterTransition
          >
            <Fade in={modalOpen}>
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, maxWidth: 400 }}>
                <Typography variant="h6" id="modal-title" align="center" >
                  Choose slot for stamp:
                </Typography>
                <Grid container spacing={2} sx={{mt: 2}}>
                  {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((slot) => (
                    <Grid item xs={6} sm={3} key={slot}>
                      <Card>
                        <CardActionArea onClick={() => handleSlotSelect(slot)}>
                          <CardContent>
                            <Typography variant="h5" align="center" sx={{fontWeight: 'bold'}}>
                              {slot.toUpperCase()}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Fade>
          </Modal>
        )}
      </div>
    </Container>
  );

};

export default CustomizeEditSv7;
